import {
  AccountAddOnServices,
  AdditionalRecipients,
  ContactDetails,
  DataInformation,
  DefaultedAccountFeatures,
  DefaultedAdditionalRecipients,
  DraftAccountDetails,
  FeaturesAndServices,
  ICashSweep,
  IReportingParameters,
  ITaxParameter,
  PartyAddress,
  ReferenceData,
  RulesType
} from '../utils/types';
import {
  getAccountsAddress,
  getAccountServices,
  getContactDetails,
  getAdditionalRecipients,
  getReferenceDataLabel
} from './createDataFromDraft';

const getTaxParameter = (feature: DefaultedAccountFeatures, refTaxCostMethodList: ReferenceData[]): ITaxParameter => ({
  taxProfileName: 'New Tax Profile Name',
  newTaxProfileName: feature?.taxProfileName,
  // taxYearEnd: feature?.taxYearEnd,
  marketDiscountAccretionMethodType: feature?.marketDiscountAccretionReportingType,
  marketDiscountAccretionReportingType: feature?.marketDiscountAccretionReportingType,
  marketDiscountAccretionFrequencyType: feature?.marketDiscountAccretionFrequencyType,
  originalIssueDiscountProcessType: feature?.originalIssueDiscountProcessType,
  originalIssueDiscountFrequencyType: feature?.originalIssueDiscountFrequencyType,
  taxExemptPremiumProcessType: feature?.taxExemptPremiumProcessType,
  taxExemptPremiumMethodType: feature?.taxExemptPremiumMethodType,
  taxExemptPremiumFrequencyType: feature?.taxExemptPremiumFrequencyType,
  taxablePremiumProcessType: feature?.taxExemptPremiumProcessType,
  taxablePremiumMethodType: feature?.taxExemptPremiumMethodType,
  taxablePremiumFrequencyType: feature?.taxablePremiumFrequencyType,
  taxCostMethodType: refTaxCostMethodList?.find((item: ReferenceData) => item.value === feature?.taxCostMethodType)
    ?.label as string,
  taxCostMethodTypeCode: feature?.taxCostMethodType,
  baseCurrencyForReporting: feature?.baseCurrencyForReporting,
  lotAccountingTypeCodeType: feature?.lotAccountingType,
  baseGlobalCurrencyCodeType: feature?.callAccountInterestCurrency,
  foreignExchangeEvalForwardsIndicator: feature?.foreignExchangeEvalForwardsIndicator,
  averageCostGainOrLossForCashIndicator: feature?.averageCostGainOrLossForCashIndicator,
  principalAndIncomeRecognitionIndicator: feature?.principalAndIncomeRecognitionIndicator
  // reviewedWithTaxAdvisor: feature?.reviewedWithTaxAdvisor
});

const getReportingParameters = (
  feature: DefaultedAccountFeatures,
  refFrequencyReceiveTypeList: ReferenceData[]
): IReportingParameters => {
  const { delegates, frequency } = feature;
  const { contactDetails, address } = delegates as DefaultedAdditionalRecipients;

  return {
    businessDays: feature?.businessDays,
    deliveryStatement: feature?.electronicStatementIndicator,
    defaultRecipients: [
      {
        name: delegates?.firstName,
        middleName: delegates?.middleName,
        lastName: delegates?.lastName,
        frequency: [...getReferenceDataLabel(refFrequencyReceiveTypeList, frequency as string[])],
        frequencyCode: frequency,
        ...getAccountsAddress(address as PartyAddress),
        ...getContactDetails(contactDetails as ContactDetails[])
      }
    ],
    recipientDetails: [
      ...getAdditionalRecipients(feature?.additionalRecipients as AdditionalRecipients[], refFrequencyReceiveTypeList)
    ]
  };
};

const getAccountFeatures = (
  accountFeatures: DefaultedAccountFeatures[],
  refFrequencyReceiveTypeList: ReferenceData[],
  refTaxCostMethodList: ReferenceData[]
): FeaturesAndServices => {
  let cashSweepOptions: ICashSweep[] = [];
  let reportingParameters: IReportingParameters[] = [];
  let taxParameters: ITaxParameter[] = [];
  accountFeatures?.forEach((feature: DefaultedAccountFeatures) => {
    if (feature.accountFeaturesType === RulesType.CASH_SWEEP) {
      cashSweepOptions = [{ portfolio: feature?.portfolio, funds: feature.funds }];
    }

    if (feature.accountFeaturesType === RulesType.REPORTING_PARAMETERS) {
      reportingParameters = [{ ...getReportingParameters(feature, refFrequencyReceiveTypeList) }];
    }

    if (feature.accountFeaturesType === RulesType.TAX_ACCOUNTING_PARAMETER) {
      taxParameters = [{ ...getTaxParameter(feature, refTaxCostMethodList) }];
    }
  });

  return {
    cashSweepOptions,
    reportingParameters,
    taxParameters
  };
};

export const getDefaultedFeaturesAndServices = (
  accounts: DraftAccountDetails[],
  referenceData: DataInformation
): FeaturesAndServices => {
  const { refFrequencyReceiveTypeList, refTaxCostMethodList } = referenceData;
  const [accountObject] = accounts;
  const { accountFeatures, accountAddOnServices } = accountObject;

  const { cashSweepOptions, reportingParameters, taxParameters } = getAccountFeatures(
    accountFeatures as DefaultedAccountFeatures[],
    refFrequencyReceiveTypeList as ReferenceData[],
    refTaxCostMethodList as ReferenceData[]
  );
  const { reportingAnalytics, dataFeed } = getAccountServices(accountAddOnServices as AccountAddOnServices[]);

  return {
    cashSweepOptions,
    reportingParameters,
    taxParameters,
    reportingAnalytics,
    dataFeed
  };
};
